import {
  type AppearanceVariant,
  type ChromeTheme,
  CROWDEX_CHROME,
  normalizeChromeTheme,
} from './chrome-theme';
import { chroma, contrastRatio, hue, normalizeOpaqueHex } from './color';

/** A VS Code color theme (`*-color-theme.json`), as far as the app reads it. */
export type VscodeTheme = Readonly<{
  name: string;
  type: AppearanceVariant;
  colors: Readonly<Record<string, string>>;
  tokenColors: readonly unknown[];
  semanticHighlighting?: boolean;
  semanticTokenColors?: Readonly<Record<string, unknown>>;
  /** A Crowdex extension of the format: the chrome the theme was designed with. */
  chrome?: ChromeTheme;
}>;

/** Comments and trailing commas removed: VS Code theme files are JSON with comments. */
function stripJsonComments(text: string): string {
  let out = '';
  let inString = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (inString) {
      out += char;
      if (char === '\\') {
        out += next ?? '';
        index += 1;
      } else if (char === '"') inString = false;
    } else if (char === '"') {
      inString = true;
      out += char;
    } else if (char === '/' && next === '/') {
      while (index < text.length && text[index] !== '\n') index += 1;
      out += '\n';
    } else if (char === '/' && next === '*') {
      index += 2;
      while (index < text.length && !(text[index] === '*' && text[index + 1] === '/')) index += 1;
      index += 1;
    } else out += char;
  }
  return out.replace(/,(\s*[}\]])/g, '$1');
}

const MAX_THEME_BYTES = 1_000_000;

/**
 * A VS Code theme from its file content or parsed JSON. Throws when it is not one. `include`
 * (a base theme by relative path) is not followed: such a theme keeps only its own colors.
 */
export function parseVscodeTheme(
  input: string | unknown,
  fallbackName = 'Imported theme',
  /** The variant its source declares (a plugin's manifest), which wins over the file. */
  variant?: AppearanceVariant,
): VscodeTheme {
  if (typeof input === 'string' && input.length > MAX_THEME_BYTES) {
    throw new Error('The theme file is too large.');
  }
  const value: unknown = typeof input === 'string' ? JSON.parse(stripJsonComments(input)) : input;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('A VS Code theme is a JSON object.');
  }
  const source = value as Record<string, unknown>;
  const colors: Record<string, string> = {};
  if (typeof source['colors'] === 'object' && source['colors'] !== null) {
    for (const [key, color] of Object.entries(source['colors'] as Record<string, unknown>)) {
      if (typeof color === 'string' && /^#[0-9a-f]{3,8}$/i.test(color.trim()))
        colors[key] = color.trim();
    }
  }
  const tokenColors = Array.isArray(source['tokenColors'])
    ? source['tokenColors']
    : Array.isArray(source['settings'])
      ? source['settings']
      : [];
  if (Object.keys(colors).length === 0 && tokenColors.length === 0) {
    throw new Error('The theme has neither colors nor token colors.');
  }
  const declaredType = typeof source['type'] === 'string' ? source['type'].toLowerCase() : '';
  const background = normalizeOpaqueHex(colors['editor.background']);
  const type: AppearanceVariant = variant
    ? variant
    : declaredType === 'light' || declaredType === 'hc-light'
      ? 'light'
      : declaredType === 'dark' || declaredType === 'hc' || declaredType === 'hc-black'
        ? 'dark'
        : background && contrastRatio(background, '#ffffff') < contrastRatio(background, '#000000')
          ? 'light'
          : 'dark';
  const name =
    typeof source['displayName'] === 'string' && source['displayName'].trim()
      ? source['displayName'].trim()
      : typeof source['name'] === 'string' && source['name'].trim()
        ? source['name'].trim()
        : fallbackName;
  return {
    name: name.slice(0, 80),
    type,
    colors,
    tokenColors,
    ...(typeof source['semanticHighlighting'] === 'boolean'
      ? { semanticHighlighting: source['semanticHighlighting'] }
      : {}),
    ...(typeof source['semanticTokenColors'] === 'object' && source['semanticTokenColors'] !== null
      ? { semanticTokenColors: source['semanticTokenColors'] as Record<string, unknown> }
      : {}),
    ...(source['chrome'] === undefined
      ? {}
      : { chrome: normalizeChromeTheme(source['chrome'], type) }),
  };
}

// Where each chrome color is read from, in order: the keys themes most reliably set.
const SURFACE_KEYS = [
  'editor.background',
  'sideBar.background',
  'editorGroupHeader.tabsBackground',
  'panel.background',
  'activityBar.background',
];
const INK_KEYS = [
  'editor.foreground',
  'sideBarTitle.foreground',
  'sideBar.foreground',
  'foreground',
];
const ACCENT_KEYS = [
  'activityBarBadge.background',
  'textLink.foreground',
  'editorCursor.foreground',
  'focusBorder',
  'button.background',
  'activityBar.activeBorder',
];
const ADDED_KEYS = [
  'gitDecoration.addedResourceForeground',
  'gitDecoration.untrackedResourceForeground',
  'terminal.ansiGreen',
  'terminal.ansiBrightGreen',
];
const REMOVED_KEYS = [
  'gitDecoration.deletedResourceForeground',
  'terminal.ansiRed',
  'terminal.ansiBrightRed',
];
const SKILL_KEYS = ['charts.purple', 'terminal.ansiMagenta', 'terminal.ansiBrightMagenta'];

function firstColor(colors: Readonly<Record<string, string>>, keys: readonly string[]) {
  for (const key of keys) {
    const color = normalizeOpaqueHex(colors[key]);
    if (color) return color;
  }
  return null;
}

/** Every token foreground of the theme, to look for a hue its UI colors do not name. */
function tokenForegrounds(theme: VscodeTheme): string[] {
  const found = new Set<string>();
  for (const rule of theme.tokenColors) {
    const settings = (rule as { settings?: { foreground?: unknown } } | null)?.settings;
    const color = normalizeOpaqueHex(
      typeof settings?.foreground === 'string' ? settings.foreground : null,
    );
    if (color) found.add(color);
  }
  return [...found];
}

type HueRange = Readonly<{ min: number; max: number; target: number }>;
const GREEN: HueRange = { min: 120, max: 170, target: 145 };
const RED: HueRange = { min: 0, max: 40, target: 25 };
const PURPLE: HueRange = { min: 270, max: 330, target: 300 };

function inRange(value: number, range: HueRange) {
  return range.min <= range.max
    ? value >= range.min && value <= range.max
    : value >= range.min || value <= range.max;
}

/** The theme's token color closest to a hue, legible on its surface. */
function colorOfHue(theme: VscodeTheme, surface: string, range: HueRange): string | null {
  let best: { color: string; distance: number } | null = null;
  for (const color of tokenForegrounds(theme)) {
    const value = hue(color);
    if (value === null || chroma(color) < 0.06 || !inRange(value, range)) continue;
    if (contrastRatio(color, surface) < 3) continue;
    const distance = Math.min(Math.abs(value - range.target), 360 - Math.abs(value - range.target));
    if (!best || distance < best.distance) best = { color, distance };
  }
  return best?.color ?? null;
}

/**
 * The chrome a VS Code theme gives the app: its own `chrome` block when it has one, otherwise
 * its editor background and foreground, its accent, and diff colors read from its keys, then
 * looked for among its token colors, then the defaults.
 */
export function chromeFromVscodeTheme(theme: VscodeTheme): ChromeTheme {
  if (theme.chrome) return theme.chrome;
  const variant = theme.type;
  const base = CROWDEX_CHROME[variant];
  const surface = firstColor(theme.colors, SURFACE_KEYS) ?? base.surface;
  const ink = firstColor(theme.colors, INK_KEYS) ?? base.ink;
  const accent =
    ACCENT_KEYS.map((key) => normalizeOpaqueHex(theme.colors[key])).find(
      (color): color is string =>
        color !== null &&
        color !== surface &&
        color !== ink &&
        contrastRatio(color, surface) >= 1.5,
    ) ?? ink;
  const semantic = (keys: readonly string[], range: HueRange, fallback: string) => {
    const keyed = firstColor(theme.colors, keys);
    return keyed && contrastRatio(keyed, surface) >= 2
      ? keyed
      : (colorOfHue(theme, surface, range) ?? fallback);
  };
  return {
    accent,
    ink,
    surface,
    contrast: base.contrast,
    fonts: { ui: null, code: null },
    semanticColors: {
      diffAdded: semantic(ADDED_KEYS, GREEN, base.semanticColors.diffAdded),
      diffRemoved: semantic(REMOVED_KEYS, RED, base.semanticColors.diffRemoved),
      skill: semantic(SKILL_KEYS, PURPLE, base.semanticColors.skill),
    },
  };
}

/** xterm's palette (concrete sRGB values) from a VS Code theme's terminal colors. */
export type TerminalPalette = Readonly<Record<string, string>>;

const ANSI = [
  'Black',
  'Red',
  'Green',
  'Yellow',
  'Blue',
  'Magenta',
  'Cyan',
  'White',
  'BrightBlack',
  'BrightRed',
  'BrightGreen',
  'BrightYellow',
  'BrightBlue',
  'BrightMagenta',
  'BrightCyan',
  'BrightWhite',
] as const;

/** The theme's terminal palette over `fallback`, with its chrome's surface and ink as the base. */
export function terminalPaletteFromVscodeTheme(
  theme: VscodeTheme,
  chrome: ChromeTheme,
  fallback: TerminalPalette,
): TerminalPalette {
  const colors = theme.colors;
  const palette: Record<string, string> = { ...fallback };
  palette['background'] =
    firstColor(colors, ['terminal.background', 'panel.background']) ?? chrome.surface;
  palette['foreground'] = firstColor(colors, ['terminal.foreground']) ?? chrome.ink;
  palette['cursor'] = firstColor(colors, ['terminalCursor.foreground']) ?? palette['foreground'];
  const selection = colors['terminal.selectionBackground'] ?? colors['editor.selectionBackground'];
  if (selection) palette['selectionBackground'] = selection;
  for (const name of ANSI) {
    const color = normalizeOpaqueHex(colors[`terminal.ansi${name}`]);
    if (color) palette[name.charAt(0).toLowerCase() + name.slice(1)] = color;
  }
  return palette;
}
